import { Box, Button, HStack, Typography, VStack } from "components/ui";
import { Formik } from "formik";
import { ArrowLeft, ArrowRight, Check, CheckCircle } from "lucide-react-native";
import React, { useState } from "react";
import { Pressable } from "react-native";
import { Toast } from "react-native-toast-notifications";
import { useSiteRegion } from "utils/hooks/useSiteSection";
import { useToken } from "utils/hooks/useToken";
import * as Yup from "yup";
import FormField from "./FormField";

interface FormValues {
	firstName: string;
	lastName: string;
	businessName: string;
	position: string;
	mobileNumber: string;
	email: string;
	state: string;
	responseMethod: string;
	industry: string;
	customerType: string;
	requestType: string;
	message: string;
}

const validationSchema = Yup.object().shape({
	// Step 1 validation
	firstName: Yup.string().required("First name is required"),
	lastName: Yup.string().required("Last name is required"),
	businessName: Yup.string().required("Business name is required"),
	position: Yup.string().required("Your position is required"),
	mobileNumber: Yup.string()
		.matches(/^[0-9\s]+$/, "Mobile number must contain only numbers and spaces")
		.required("Mobile number is required"),
	email: Yup.string().email("Invalid email").required("Work email is required"),

	// Step 2 validation
	state: Yup.string().required("State is required"),
	responseMethod: Yup.string().required("Please select how you would like us to respond"),
	industry: Yup.string().required("Industry is required"),
	customerType: Yup.string().required("Customer type is required"),
	requestType: Yup.string().required("Please select request type"),
	message: Yup.string().required("Message is required"),
});

const initialValues: FormValues = {
	firstName: "",
	lastName: "",
	businessName: "",
	position: "",
	mobileNumber: "",
	email: "",
	state: "",
	responseMethod: "",
	industry: "",
	customerType: "",
	requestType: "",
	message: "",
};

const stateOptions = [
	{ value: "nsw", label: "New South Wales" },
	{ value: "vic", label: "Victoria" },
	{ value: "qld", label: "Queensland" },
	{ value: "sa", label: "South Australia" },
	{ value: "wa", label: "Western Australia" },
	{ value: "tas", label: "Tasmania" },
	{ value: "nt", label: "Northern Territory" },
	{ value: "act", label: "Australian Capital Territory" },
];

const responseOptions = [
	{ value: "email", label: "Email" },
	{ value: "phone", label: "Phone Call" },
	{ value: "sms", label: "SMS" },
];

const industryOptions = [
	{ value: "retail", label: "Retail" },
	{ value: "manufacturing", label: "Manufacturing" },
	{ value: "healthcare", label: "Healthcare" },
	{ value: "education", label: "Education" },
	{ value: "technology", label: "Technology" },
	{ value: "construction", label: "Construction" },
	{ value: "other", label: "Other" },
];

const customerTypeOptions = [
	{ value: "individual", label: "Individual" },
	{ value: "business", label: "Business" },
	{ value: "government", label: "Government" },
	{ value: "nonprofit", label: "Non-Profit" },
];

const requestTypeOptions = [
	{ value: "demo", label: "Request Demo" },
	{ value: "callback", label: "Request Call Back" },
];

const EnquireForm = ({ setIsOpenEnquireNow }: { setIsOpenEnquireNow: (isOpen: boolean) => void }) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isValidating, setIsValidating] = useState(false);
	const region = useSiteRegion();
	const totalSteps = 3;
	const { token } = useToken();

	const handleNext = async (formik: any) => {
		if (isValidating) return; // Prevent double clicks

		setIsValidating(true);

		const currentStepFields = currentStep === 1 ? ["firstName", "lastName", "email", "mobileNumber"] : currentStep === 2 ? ["businessName", "position", "state", "industry", "customerType"] : ["responseMethod", "requestType", "message"];

		const touched = currentStepFields.reduce(
			(acc, field) => ({
				...acc,
				[field]: true,
			}),
			{},
		);

		// Set touched fields and wait for state to update
		await formik.setTouched(touched);

		// Small delay to ensure validation state is updated
		await new Promise((resolve) => setTimeout(resolve, 100));

		const errors = await formik.validateForm();
		const hasStepErrors = currentStepFields.some((field) => errors[field]);

		if (!hasStepErrors) {
			setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
		}

		setIsValidating(false);
	};

	const handleBack = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 1));
	};

	const handleSubmit = async (values: FormValues, formikBag: any) => {
		if (isSubmitting) return; // Prevent double clicks

		// Ensure all fields are touched and validated before submitting
		const allFields = Object.keys(values);
		const touched = allFields.reduce((acc, field) => ({ ...acc, [field]: true }), {});
		await formikBag.setTouched(touched);

		// Small delay to ensure validation state is updated
		await new Promise((resolve) => setTimeout(resolve, 100));

		const errors = await formikBag.validateForm();
		const hasErrors = Object.keys(errors).length > 0;

		if (hasErrors) {
			return; // Don't submit if there are validation errors
		}

		setIsSubmitting(true);
		let comments = "";
		for (let key in values) {
			if (key !== "requestType") {
				comments += `${key}: ${values[key as keyof FormValues]}\n`;
			}
		}
		//add the url to the comments
		comments += `URL: ${window.location.href}\n`;
		setIsSubmitting(true);

		// Get current URL without protocol for tracking
		const getCurrentUrlWithoutProtocol = () => {
			var currentUrl = window.location.href;
			return currentUrl.replace(/^https?:\/\//i, "");
		};
		let currentUrl = getCurrentUrlWithoutProtocol();

		let newData: any = {
			email: values.email,
			firstname: values.firstName,
			lastname: values.lastName,
			memo: comments,
			phone: values.mobileNumber,
			website: "1",
			custbody_esc_campaign_category: -4, // Default category
			leadsource: -6,
			customerType: values.customerType === "Private" ? 1 : 2,
			requestDemo: values.requestType === "demo" ? true : false,
			url: currentUrl, // URL tracking
			subsidiary: region === "au" ? 2 : 6,
		};

		if (token !== null) {
			// Check for adaccount parameter - if present, mark as paid keyword
			if (token["adaccount"]) {
				newData["custbody_esc_campaign_category"] = -15; // Marking as paid keyword
				newData["paid_keyword"] = true;
			}

			if (token["campaign"]) {
				newData["custbodyabco_campaign_name"] = token["campaign"];
				if (newData["custbody_esc_campaign_category"] !== -15) {
					newData["custbody_esc_campaign_category"] = -11;
				}
			} else if (token["utm_campaign"]) {
				if (newData["custbody_esc_campaign_category"] !== -15) {
					newData["custbody_esc_campaign_category"] = -11;
				}
			}

			if (token["gclid"] && newData["custbody_esc_campaign_category"] !== -15) {
				newData["custbody_esc_campaign_category"] = -15;
			}
		}

		if (typeof window !== "undefined") {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "form_submit",
				form_id: "1",
				form_name: "enquiry_form",
			});
		}

		let url = `https://6763524-sb2.extforms.netsuite.com/app/site/hosting/scriptlet.nl?script=3368&deploy=1&compid=6763524_SB2&ns-at=AAEJ7tMQ78nFVV1hfo0N29vYjqvAXm5fL2AVK8GRSQDB97yO6VI&action=createOpportunity`;
		if (process.env.NODE_ENV === "production") {
			url = `https://6763524.extforms.netsuite.com/app/site/hosting/scriptlet.nl?script=3368&deploy=1&compid=6763524&ns-at=AAEJ7tMQscoLnHY3TQgUcIofUDhsW0KGKedD0ocvSoJn0oybK3M&action=createOpportunity`;
		}

		fetch(url, {
			method: "POST",
			mode: "no-cors",
			headers: {
				"Content-Type": "application/json",
				"User-Agent": "Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/102.0.0.0 Safari/537.36",
			},
			body: JSON.stringify(newData),
		})
			.then((res) => {
				setIsSubmitting(false);
				setIsSubmitted(true);
				setTimeout(() => {
					setIsOpenEnquireNow(false);
				}, 2000);
			})
			.catch((err) => {
				setIsSubmitting(false);
				Toast.show("Something went wrong. Please try again.", {
					type: "danger",
					duration: 3000,
				});
			});
	};

	return (
		<Box>
			<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values, formikBag) => handleSubmit(values, formikBag)}>
				{(formik) => (
					<Box justifyContent="space-between" height={"$full"}>
						{isSubmitted ? (
							<VStack py={24} alignItems="center" justifyContent="center" flex={1} gap={16}>
								<CheckCircle size={48} color="green" />
								<Typography.H3>Submitted Successfully!</Typography.H3>
								<Typography.ParagraphSmall color="$neutral600">Thank you for your enquiry. We'll get back to you soon.</Typography.ParagraphSmall>
							</VStack>
						) : (
							<>
								<Box position="relative" zIndex={10}>
									<HStack justifyContent="center" alignItems="center" mb={32}>
										{[1, 2, 3].map((step, idx, arr) => {
											const isCompleted = step < currentStep;
											const isCurrent = step === currentStep;
											const StepCircle = (
												<Box
													width={32}
													height={32}
													borderRadius={16}
													borderWidth={2}
													borderColor={isCurrent ? "$primary" : isCompleted ? "$primary" : "$neutral400"}
													alignItems="center"
													justifyContent="center"
													bg={isCurrent ? "$primary" : isCompleted ? "$basicwhite" : "$basicwhite"}
												>
													{isCompleted ? <Check size={16} color="#1A428A" /> : <Typography.H5 color={isCurrent ? "$basicwhite" : isCompleted ? "$primary" : "$neutral600"}>{step}</Typography.H5>}
												</Box>
											);
											return (
												<React.Fragment key={step}>
													{isCompleted ? <Pressable onPress={() => setCurrentStep(step)}>{StepCircle}</Pressable> : StepCircle}
													{idx < arr.length - 1 && <Box flex={1} height={2} bg="$neutral300" mx={2} />}
												</React.Fragment>
											);
										})}
									</HStack>
									{currentStep === 1 && (
										<VStack gap={16}>
											<Box>
												<Typography.H3 color="$primary">Personal Information</Typography.H3>
												<Typography.ParagraphSmall color="$neutral600">Please fill in the following details to help us better understand your needs.</Typography.ParagraphSmall>
											</Box>
											<FormField label="First Name*" name="firstName" placeholder="Enter your first name" formik={formik} />
											<FormField label="Last Name*" name="lastName" placeholder="Enter your last name" formik={formik} />
											<FormField label="Email*" name="email" placeholder="example@address.com" formik={formik} />
											<FormField label="Mobile Number*" name="mobileNumber" placeholder="0400 000 000" formik={formik} />
										</VStack>
									)}

									{currentStep === 2 && (
										<VStack gap={16}>
											<Typography.H3>Business Details</Typography.H3>
											<FormField label="Business Name*" name="businessName" placeholder="Enter your business name" formik={formik} />
											<FormField label="Your Position*" name="position" placeholder="Enter your position" formik={formik} />
											<Box position="relative" zIndex={9}>
												<FormField label="State*" name="state" type="select" options={stateOptions} placeholder="--Please Select--" formik={formik} />
											</Box>
											<Box position="relative" zIndex={8}>
												<FormField label="Industry*" name="industry" type="select" options={industryOptions} placeholder="--Please Select--" formik={formik} />
											</Box>
											<Box position="relative" zIndex={7}>
												<FormField label="Customer Type" name="customerType" type="select" options={customerTypeOptions} placeholder="--Please Select--" formik={formik} />
											</Box>
										</VStack>
									)}

									{currentStep === 3 && (
										<VStack gap={16}>
											<Typography.H3>Additional Information</Typography.H3>
											<Box position="relative" zIndex={9}>
												<FormField label="How would you like us to respond?*" name="responseMethod" type="select" options={responseOptions} placeholder="--Please Select--" formik={formik} />
											</Box>
											<Box position="relative" zIndex={8}>
												<FormField label="Request Demo or Call Back" name="requestType" type="select" options={requestTypeOptions} placeholder="--Please Select--" formik={formik} />
											</Box>
											<Box position="relative" zIndex={7}>
												<FormField label="Your Message*" name="message" placeholder="Enter your message" type="input" formik={formik} />
											</Box>
										</VStack>
									)}
								</Box>
								<HStack position="relative" zIndex={6} justifyContent="space-between" mt={24}>
									{currentStep > 1 && (
										<Button gap={4} variant="secondary" onPress={handleBack}>
											<ArrowLeft />
											<Typography weight="bold">Back</Typography>
										</Button>
									)}
									{currentStep < totalSteps ? (
										<Button gap={4} ml="auto" variant="secondary" onPress={() => handleNext(formik)}>
											<Typography weight="bold">Next</Typography>
											<ArrowRight />
										</Button>
									) : (
										<Button variant="secondary" onPress={() => formik.handleSubmit()} disabled={isSubmitting}>
											{isSubmitting ? <Typography weight="bold">Submitting...</Typography> : <Typography weight="bold">Submit</Typography>}
										</Button>
									)}
								</HStack>
							</>
						)}
					</Box>
				)}
			</Formik>
		</Box>
	);
};

export default EnquireForm;
